import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Download } from 'lucide-react';
import { apiClient } from '../../apiClient';
import { useUser } from '../../hooks/UserContext';

const UserManagement = () => {
    const { systemAdminRole } = useUser();
    const isAdminRole = systemAdminRole === 'admin';

    const initialFilters = {
        billwave_customer_id: '',
        company_name: '',
        name: '',
        email: ''
    };

    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [lastDocId, setLastDocId] = useState(null);
    const [hasMore, setHasMore] = useState(true);
    const [filters, setFilters] = useState(initialFilters);
    const [error, setError] = useState(null);

    const fetchUsers = useCallback(async (isNext = false) => {
        setLoading(true);
        setError(null);
        try {
            const queryParams = new URLSearchParams();
            Object.entries(filters).forEach(([key, value]) => {
                if (value) queryParams.append(key, value);
            });
            queryParams.append('limit', 50);
            if (isNext && lastDocId) {
                queryParams.append('lastDocId', lastDocId);
            }

            const response = await apiClient.get(`/v1/admin/users?${queryParams.toString()}`);
            const data = await response.json();

            if (response.ok) {
                if (isNext) {
                    setUsers(prev => [...prev, ...data]);
                } else {
                    setUsers(data);
                }
                if (data.length < 50) {
                    setHasMore(false);
                } else {
                    setHasMore(true);
                    setLastDocId(data[data.length - 1].uid);
                }
            } else {
                setError(data.error || "データの取得に失敗しました。");
            }
        } catch (error) {
            console.error("Error fetching users:", error);
            setError("通信エラーが発生しました。");
        } finally {
            setLoading(false);
        }
    }, [filters, lastDocId]);

    useEffect(() => {
        fetchUsers();
    }, []);

    const handleSearch = (e) => {
        if (e) e.preventDefault();
        setLastDocId(null);
        setHasMore(true);
        fetchUsers();
    };

    const handleReset = () => {
        setFilters(initialFilters);
        setLastDocId(null);
        setHasMore(true);
        setTimeout(() => {
            fetchUsers();
        }, 0);
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const handleCsvExport = async () => {
        if (!isAdminRole) return;
        setExporting(true);
        setError(null);
        try {
            const queryParams = new URLSearchParams();
            Object.entries(filters).forEach(([key, value]) => {
                if (value) queryParams.append(key, value);
            });

            const response = await apiClient.get(`/v1/admin/users/export/csv?${queryParams.toString()}`);

            if (!response.ok) {
                const errData = await response.json();
                throw new Error(errData.error || 'CSVのエクスポートに失敗しました。');
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `user_list_${new Date().toISOString().split('T')[0]}.csv`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (err) {
            console.error('Export error:', err);
            setError(err.message || 'CSVのエクスポート中にエラーが発生しました。');
        } finally {
            setExporting(false);
        }
    };

    return (
        <div className="container mx-auto px-4 py-8">
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">ユーザー管理</h2>
                {isAdminRole && (
                    <button
                        type="button"
                        onClick={handleCsvExport}
                        disabled={exporting}
                        className="flex items-center bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 font-bold text-sm shadow-sm disabled:bg-green-300"
                    >
                        <Download className="w-4 h-4 mr-2" />
                        {exporting ? 'CSV出力中...' : 'CSVダウンロード'}
                    </button>
                )}
            </div>

            {error && (
                <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-8">
                    <p className="text-sm text-red-700 font-bold">{error}</p>
                </div>
            )}

            <form onSubmit={handleSearch} className="bg-white p-6 rounded-lg shadow-md mb-8">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700">BillWaveお客様ID</label>
                        <input
                            type="text"
                            name="billwave_customer_id"
                            value={filters.billwave_customer_id}
                            onChange={handleFilterChange}
                            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">企業名</label>
                        <input
                            type="text"
                            name="company_name"
                            value={filters.company_name}
                            onChange={handleFilterChange}
                            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">氏名</label>
                        <input
                            type="text"
                            name="name"
                            value={filters.name}
                            onChange={handleFilterChange}
                            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">メールアドレス</label>
                        <input
                            type="text"
                            name="email"
                            value={filters.email}
                            onChange={handleFilterChange}
                            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        />
                    </div>
                </div>
                <div className="mt-4 flex justify-end gap-2">
                    <button
                        type="submit"
                        className="bg-indigo-600 text-white px-8 py-2 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 font-bold shadow-sm"
                    >
                        検索
                    </button>
                    <button
                        type="button"
                        onClick={handleReset}
                        className="bg-gray-100 text-gray-700 px-6 py-2 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 font-bold"
                    >
                        リセット
                    </button>
                </div>
            </form>

            <div className="bg-white shadow overflow-x-auto sm:rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">BillWaveお客様ID</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">企業名</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ロール</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">部署名</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">氏名</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">メールアドレス</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">操作</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {users.map(user => (
                            <tr key={user.uid}>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.billwave_customer_id}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{user.company_name}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                        user.role === 'admin' || user.role === 'owner' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                                    }`}>
                                        {user.role === 'admin' || user.role === 'owner' ? '管理者' : 'メンバー'}
                                    </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.department}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{user.name}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.email}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                    <Link to={`/admin/users/${user.uid}`} className="text-indigo-600 hover:text-indigo-900">
                                        詳細
                                    </Link>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {hasMore && (
                <div className="mt-6 flex justify-center">
                    <button
                        onClick={() => fetchUsers(true)}
                        disabled={loading}
                        className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                        {loading ? '読み込み中...' : '次の50件を読み込む'}
                    </button>
                </div>
            )}
        </div>
    );
};

export default UserManagement;
